import InfiniteScroll from 'react-infinite-scroll-component';
import useOrgActivity from './useOrgActivity';
import GalleryPreviewImage from '@/src/Gallery/components/GalleryPreviewImage';
import Loading from '@/src/Common/components/Loading';
import { format } from 'date-fns';
import { useState, useEffect } from 'react';
import ActivationStatusCard from './ActivationStatusCard';

export default function OrgActivityFeed() {
    const { feed } = useOrgActivity();
    const [expandedIndex, setExpandedIndex] = useState<ShareIdentifier | null>(
        null,
    );
    const [isFullScreen, setIsFullScreen] = useState(false);

    const toggleFullScreen = () => {
        setIsFullScreen(!isFullScreen);
    };

    // Add ESC key support to close full screen
    useEffect(() => {
        const handleKeyDown = (event: KeyboardEvent) => {
            if (event.key === 'Escape' && isFullScreen) {
                setIsFullScreen(false);
            }
        };

        if (isFullScreen) {
            document.addEventListener('keydown', handleKeyDown);
        }

        return () => {
            document.removeEventListener('keydown', handleKeyDown);
        };
    }, [isFullScreen]);

    if (isFullScreen) {
        return (
            <div className="fixed inset-0 bg-background z-[100] overflow-hidden top-[68px]">
                <header className="flex sticky items-center justify-between border-b border-border py-4 px-6">
                    <h1 className="text-2xl font-bold tracking-tight text-foreground">
                        Command Center
                    </h1>
                    <div className="flex items-center gap-3">
                        <span className="text-sm text-muted-foreground">
                            Press ESC to close
                        </span>
                        <button
                            onClick={toggleFullScreen}
                            className="flex items-center gap-x-2 rounded-md  px-4 py-2 text-sm font-semibold transition-colors"
                        >
                            <svg
                                className="w-6 h-6"
                                fill="none"
                                stroke="currentColor"
                                viewBox="0 0 24 24"
                            >
                                <path
                                    strokeLinecap="round"
                                    strokeLinejoin="round"
                                    strokeWidth={2}
                                    d="M6 18L18 6M6 6l12 12"
                                />
                            </svg>
                        </button>
                    </div>
                </header>
                <div className="h-full overflow-y-auto p-6">
                    <div className="grid grid-cols-2 md:grid-cols-3 lg:grid-cols-4 xl:grid-cols-5 gap-6">
                        {feed.map((item) => (
                            <div
                                key={item.id}
                                className="flex flex-col space-y-3"
                            >
                                {/* Activation info card */}
                                <ActivationStatusCard
                                    activationId={item?.activationId}
                                    activationName={item?.activationName}
                                    createdAt={item?.createdAt}
                                />

                                {/* Photo */}
                                <div className="aspect-square">
                                    <GalleryPreviewImage
                                        media={item}
                                        isExpanded={
                                            item.shareIdentifier ===
                                            expandedIndex
                                        }
                                        setExpandedIndex={setExpandedIndex}
                                        index={item.shareIdentifier}
                                    />
                                </div>
                            </div>
                        ))}
                    </div>
                    {feed.length === 0 && (
                        <div className="text-center py-12">
                            <p className="text-muted-foreground">
                                No recent media found.
                            </p>
                        </div>
                    )}
                </div>
            </div>
        );
    }

    // Sidebar list view
    return (
        <div className="max-w-8xl mx-auto overflow-hidden">
            <header className="flex sticky items-center justify-between border-b border-white/5 py-4 sm:py-6">
                <h1 className="text-base font-semibold text-foreground">
                    Activity
                </h1>
                <button
                    onClick={toggleFullScreen}
                    className="flex items-center gap-x-1 rounded-md bg-primary px-2 py-1 text-xs font-semibold cursor-pointer text-primary-foreground shadow-sm hover:bg-primary/90 focus-visible:outline focus-visible:outline-2 focus-visible:outline-offset-2 focus-visible:outline-blue-500 transition-colors"
                    title="Expand to grid view"
                >
                    <svg
                        className="w-3 h-3"
                        fill="none"
                        stroke="currentColor"
                        viewBox="0 0 24 24"
                    >
                        <path
                            strokeLinecap="round"
                            strokeLinejoin="round"
                            strokeWidth={2}
                            d="M4 6a2 2 0 012-2h2a2 2 0 012 2v2a2 2 0 01-2 2H6a2 2 0 01-2-2V6zM14 6a2 2 0 012-2h2a2 2 0 012 2v2a2 2 0 01-2 2h-2a2 2 0 01-2-2V6zM4 16a2 2 0 012-2h2a2 2 0 012 2v2a2 2 0 01-2 2H6a2 2 0 01-2-2v-2zM14 16a2 2 0 012-2h2a2 2 0 012 2v2a2 2 0 01-2 2h-2a2 2 0 01-2-2v-2z"
                        />
                    </svg>
                </button>
            </header>
            <div
                className="min-h-[80%] flex pl-0 pb-0 overflow-hidden max-h-full h-full"
                id="orgActivityFeed"
            >
                <InfiniteScroll
                    dataLength={feed.length}
                    style={{
                        padding: 0,
                        paddingBottom: 0,
                        overflow: 'hidden',
                    }}
                    scrollableTarget="orgActivityFeed"
                    hasMore={false}
                    loader={<Loading isLoading={true} />}
                    next={() => {}}
                >
                    {feed.map((item, itemIdx) => (
                        <div key={item.id} className="pl-0">
                            <div className="relative pb-2">
                                {itemIdx !== feed.length - 1 ? (
                                    <span
                                        className="absolute left-[50px] top-5 -ml-px h-full w-[1px] bg-border"
                                        aria-hidden="true"
                                    />
                                ) : null}
                                <div className="relative flex items-start space-x-3 pl-0">
                                    <span className="flex flex-col items-center border-[1px] border-border bg-background p-3 rounded-[4px] w-[100px] min-w-[100px]">
                                        <a
                                            href={`/activations/${item?.activationId}`}
                                        >
                                            <p className="clickable text-sm font-regular text-center text-foreground text-ellipsis">
                                                {item?.activationName}
                                            </p>
                                        </a>
                                        <p className="text-[0.5rem] font-medium text-muted-foreground">
                                            {format(item?.createdAt, 'Pp')}
                                        </p>
                                    </span>
                                    <GalleryPreviewImage
                                        media={item}
                                        isExpanded={
                                            item.shareIdentifier ===
                                            expandedIndex
                                        }
                                        setExpandedIndex={setExpandedIndex}
                                        index={item.shareIdentifier}
                                    />
                                </div>
                            </div>
                        </div>
                    ))}
                </InfiniteScroll>
            </div>
        </div>
    );
}
